import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

import {
  formatCurrency,
  copy,
  getSinglarOrPluralWord,
  formatDateTime,
  getTimezoneDate,
  formatNumber,
  getPreviewImage,
  capitalizeString,
} from '../../../utilities/helpers'

import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import Image from 'react-bootstrap/Image'
import Button from 'react-bootstrap/Button'
import Stack from 'react-bootstrap/Stack'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'

import { InfoIcon } from '../../InfoIcon'

import placeholder from '../../../assets/placeholder.png'

import './cards.scss'

export default function Cards({
  eventPackage,
  stats,
  isVisible,
  handleChange,
  blockDirectUrl,
  handleBlockDirectUrl,
  savingBlockDirectUrl,
  isDisabled,
  hasPackageEnded,
}) {
  const [copied, setCopied] = useState(false)
  const pricingTierPrices = (eventPackage?.pricingTiers || [])
    .map((pricingTier) => Number(pricingTier?.price))
    .filter((price) => Number.isFinite(price))

  const hasPricingTierPrices = pricingTierPrices.length > 0
  const lowestPricingTierPrice = hasPricingTierPrices
    ? Math.min(...pricingTierPrices)
    : null
  const highestPricingTierPrice = hasPricingTierPrices
    ? Math.max(...pricingTierPrices)
    : null

  const linkDisabled = isDisabled || blockDirectUrl

  const pricePerSeat =
    hasPricingTierPrices && lowestPricingTierPrice !== highestPricingTierPrice
      ? `${formatCurrency(lowestPricingTierPrice)} - ${formatCurrency(highestPricingTierPrice)}`
      : formatCurrency(
          hasPricingTierPrices ? lowestPricingTierPrice : eventPackage?.price
        )

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false)
      }, 2000)
    }
  }, [copied])

  return (
    <section className="cards">
      <Row>
        <Col xs={8} className="col-1">
          <Row>
            <Col>
              <Stack gap={4}>
                <Card body className="card--light">
                  <div className="card-body-heading--sm">
                    <Card.Title as="h5" className="card-title-upper">
                      Overview
                    </Card.Title>
                  </div>
                  <Row id="overview-box">
                    <Col>
                      <div className="card-body-heading heading--flex sold m-0">
                        <Stack>
                          <Stack
                            direction="horizontal"
                            className="small-label--flex"
                          >
                            <span className="small-label">Orders made</span>
                            <OverlayTrigger
                              placement="right"
                              overlay={
                                <Tooltip>Number of individual orders.</Tooltip>
                              }
                            >
                              <Button variant="link">
                                <InfoIcon variant="dark" />
                              </Button>
                            </OverlayTrigger>
                          </Stack>
                          <h5 className="card-title card-title-xl">
                            {formatNumber(stats?.packagesSold)}
                          </h5>
                        </Stack>
                      </div>
                    </Col>
                    <Col>
                      <Stack gap={1} className="price">
                        <span className="small small-bold">
                          Prices per seat
                        </span>
                        <span className="fw-semibold">{pricePerSeat}</span>
                      </Stack>
                    </Col>
                  </Row>
                  <div className="seperator">
                    <Stack direction="horizontal" gap={4}>
                      <div>
                        <div className="card-body-heading--xs">
                          <Card.Title as="h5" className="card-title-upper">
                            Seat Map
                          </Card.Title>
                          <Card.Subtitle as="h6" className="mt-2">
                            {capitalizeString(eventPackage?.seatmap?.name)}
                          </Card.Subtitle>
                        </div>
                        <Link to="seatmap" className="btn btn-primary">
                          Open Seat Map
                        </Link>
                      </div>
                      <Image
                        src={
                          getPreviewImage(eventPackage?.venue?.slug) ||
                          placeholder
                        }
                        width={250}
                        height={200}
                        className="ms-auto"
                        alt={`Picture of ${capitalizeString(eventPackage?.seatmap?.name)} Map`}
                      />
                    </Stack>
                  </div>
                </Card>
              </Stack>
            </Col>
          </Row>
          <Row>
            <Col>
              <Card className="card--md-vertical card--light">
                <Card.Body className="d-flex-column">
                  <div className="card-body-heading--xs">
                    <Card.Title as="h5" className="card-title-upper">
                      Package Listing
                    </Card.Title>
                    <Card.Subtitle
                      as="h6"
                      className="subtitle--dark mt-2 fw-medium"
                    >
                      Your package listing is now available
                    </Card.Subtitle>
                  </div>
                  <Stack
                    direction="horizontal"
                    className="mt-auto justify-content-start btn-group-flex btn-group-flex-sm"
                  >
                    <Button
                      variant="outline-light"
                      className={`copy-link-btn btn-width-xs--flex-item ${copied ? 'btn-copy--success' : ''} `}
                      onClick={() =>
                        copy(
                          `${process.env.REACT_APP_CONSUMER_PORTAL}/venue/${eventPackage?.venue?.slug}/package/${eventPackage?.uuid}`,
                          setCopied
                        )
                      }
                      disabled={linkDisabled}
                    >
                      {!copied && 'Copy link'}
                    </Button>
                    <Link
                      to={`${process.env.REACT_APP_CONSUMER_PORTAL}/venue/${eventPackage?.venue?.slug}/package/${eventPackage?.uuid}`}
                      target="_blank"
                      className={`btn btn-primary ${linkDisabled ? 'btn-link-disabled' : ''}`}
                    >
                      Open package page
                    </Link>
                  </Stack>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
        <Col xs={4} className="d-flex col-2">
          <Card className="card--md card--light">
            <Card.Body className="d-flex-column">
              <div className="card-body-heading--sm">
                <Card.Title as="h5" className="card-title-upper">
                  Package Details
                </Card.Title>
              </div>
              <Card.Img
                src={eventPackage?.image?.url || placeholder}
                width="332"
                height="145"
                alt={`Cover art for ${eventPackage?.name} package`}
                className="event-image package-img mb-3"
              />
              <h4 className="name fs-md fs-md-bold">{eventPackage?.name}</h4>
              <span className="normal normal-bold">
                {formatNumber(eventPackage?.events?.length)}{' '}
                {getSinglarOrPluralWord(eventPackage?.events?.length, 'Event')}
              </span>
              <span className="subtitle fw-medium">
                <span>
                  {formatDateTime(
                    getTimezoneDate(
                      eventPackage?.start,
                      eventPackage?.timezone
                    ),
                    'dateOnly'
                  )}
                </span>
                <span className="to">
                  {formatDateTime(
                    getTimezoneDate(eventPackage?.end, eventPackage?.timezone),
                    'dateOnly'
                  )}
                </span>
              </span>
              <Link
                to="package-details"
                className="small small-bold link-goTo my-3"
              >
                Manage details
              </Link>
              <Card
                body
                id="visibility-card"
                className="card--sm card-with-border h-auto mt-auto"
              >
                <Form.Check
                  type="switch"
                  id="package-visibility"
                  label="Visibility"
                  checked={isVisible}
                  onChange={(e) => {
                    handleChange(e, e.target.checked)
                  }}
                  disabled={isDisabled}
                  className="form-switch--with-desc gap-lg"
                />
                <small className="subtitle subtitle--dark form-switch--with-desc-subtitle gap-lg">
                  Package will be visible to attendees
                </small>
                <Form.Check
                  type="switch"
                  id="package-block-direct-url"
                  label="Block direct link"
                  checked={Boolean(blockDirectUrl)}
                  onChange={(e) => {
                    handleBlockDirectUrl(e, e.target.checked)
                  }}
                  disabled={savingBlockDirectUrl}
                  className="form-switch--with-desc gap-lg mt-3"
                />
                <small className="subtitle subtitle--dark form-switch--with-desc-subtitle gap-lg">
                  The public package page is unavailable, including for anyone
                  who already has the link
                </small>
              </Card>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </section>
  )
}
